import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle, ExternalLink } from 'lucide-react';
import { HELP_LINKS } from '../../constants/helpLinks';

const activeBarStyle = { backgroundColor: 'var(--sidebar-active-bar)' };

export default function HelpMenu({ collapsed = false, isMobile = false, onLinkClick }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const computePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    if (collapsed && !isMobile) {
      setPosition({
        left: rect.right + 8,
        bottom: window.innerHeight - rect.bottom,
        anchor: 'right',
      });
    } else {
      setPosition({
        left: rect.left,
        bottom: window.innerHeight - rect.top + 8,
        width: rect.width,
        anchor: 'top',
      });
    }
  }, [collapsed, isMobile]);

  const handleToggle = () => {
    if (!open) computePosition();
    setOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (e) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const handleReposition = () => computePosition();

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, computePosition]);

  const handleLinkClick = () => {
    setOpen(false);
    if (onLinkClick) onLinkClick();
  };

  const trigger = collapsed && !isMobile ? (
    <button
      ref={triggerRef}
      onClick={handleToggle}
      className="relative flex items-center justify-center w-full min-h-[44px] rounded-ae-xs text-sm transition-colors"
      style={{ color: 'var(--sidebar-text)' }}
      title="Help"
      aria-label="Help"
      aria-expanded={open}
      aria-haspopup="menu"
    >
      {open && <span className="absolute left-0 top-2 bottom-2 w-0.5 rounded-r" style={activeBarStyle} />}
      <HelpCircle size={20} />
    </button>
  ) : (
    <button
      ref={triggerRef}
      onClick={handleToggle}
      className="relative flex items-center gap-3 min-h-[44px] rounded-ae-xs text-sm transition-colors w-full px-3"
      style={{ color: 'var(--sidebar-text)' }}
      aria-label="Help"
      aria-expanded={open}
      aria-haspopup="menu"
    >
      <HelpCircle size={20} className="flex-shrink-0" />
      <span className="truncate min-w-0">Help</span>
    </button>
  );

  const popoverStyle = position
    ? {
        position: 'fixed',
        left: `${position.left}px`,
        bottom: `${position.bottom}px`,
        minWidth: position.anchor === 'top' ? `${Math.max(position.width || 0, 240)}px` : '260px',
        maxWidth: '320px',
        zIndex: 60,
      }
    : { display: 'none' };

  const popover = open && position ? createPortal(
    <div
      ref={popoverRef}
      role="menu"
      aria-label="Help links"
      className="rounded-ae-sm shadow-lg border bg-white py-1"
      style={{ ...popoverStyle, borderColor: 'var(--color-border, #e5e7eb)' }}
    >
      <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100">
        Help & Documentation
      </div>
      <ul className="py-1">
        {HELP_LINKS.map((link) => {
          const Icon = link.icon;
          return (
            <li key={link.url}>
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleLinkClick}
                role="menuitem"
                className="flex items-start gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {Icon && <Icon size={16} className="flex-shrink-0 mt-0.5 text-gray-500" />}
                <span className="flex-1 min-w-0">
                  <span className="block font-medium text-gray-900">{link.label}</span>
                  {link.description && (
                    <span className="block text-xs text-gray-500 truncate">{link.description}</span>
                  )}
                </span>
                <ExternalLink size={12} className="flex-shrink-0 mt-1 text-gray-400" />
              </a>
            </li>
          );
        })}
      </ul>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      {trigger}
      {popover}
    </>
  );
}
